import React from "react";
import { axiosInstance, fetchInstance } from "../../../utils/networking";
import { StyledButtonMain } from "../../common/Buttons";
import ThriveModal from "../../common/ThriveModal";
import EmailEditor from "../../common/EmailEditor";
import NoticeDialog from "../../common/NoticeDialog";
import { DirectEmailEmptyBody } from "../../common/EmailBodies/DirectEmailEmptyBody";
import DataValidationDrawer from "./DataValidationDrawer";

import Fuse from "fuse.js";

const DataValidations = () => {
    const [cases, setCases] = React.useState<any[]>([]);
    const [refreshTrigger, setRefreshTrigger] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState("");

    React.useEffect(() => {
        const fetchCasesWithFirms = async () => {
            let stream_cases: any[] = [];
            const response = await fetchInstance(
                `${process.env.REACT_APP_API_DOMAIN}/cases/recent`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        period: "12m",
                        caseStatus: "either",
                        sortBy: "recent",
                        primaryIndustry: "all",
                        ids: [],
                        sbc: "all",
                    }), // Adjust the limit as needed
                }
            );
            if (!response.body) {
                throw new Error("ReadableStream not supported in this environment.");
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || ""; // keep incomplete line

                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const obj = JSON.parse(line);
                            // Pre-process to integer for revenue if needed, or just keep as is
                            if (obj.firmRecord) {
                                ['Annual Revenue', 'Annual Revenue 2025', 'Annual Revenue 2024'].forEach(key => {
                                    if (obj.firmRecord[key]) {
                                        obj.firmRecord[key] = Math.round(obj.firmRecord[key]);
                                    }
                                });
                            }
                            stream_cases.push(obj);
                        } catch (err) {
                            console.error("Failed to parse line:", line, err);
                        }
                    }
                }
            }
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
            setCases(stream_cases.filter((item) => {
                if (!item.firmRecord.last_validated_at || new Date(item.firmRecord.last_validated_at) < sixMonthsAgo) { return true }
                return false;
            }));
        };

        fetchCasesWithFirms();

    }, [refreshTrigger]);

    const formatCurrency = (value: any) => {
        if (!value) return '-';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    };

    const [selectedCase, setSelectedCase] = React.useState<any | null>(null);
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const [directEmailOpen, setDirectEmailOpen] = React.useState(false);
    const [messageOpen, setMessageOpen] = React.useState(false);
    const [messageTitle, setMessageTitle] = React.useState("");
    const [messageBody, setMessageBody] = React.useState("");

    const handleRowClick = (item: any) => {
        setSelectedCase(item);
        setDrawerOpen(true);
    };

    const handleVerify = (data: any) => {
        let postData = {
            editedFirm: {
                openCase: selectedCase._id,
                _id: data._id,
                Employees: data.Employees ? Number(data.Employees) : 0,
                "Annual Revenue": data["Annual Revenue"] ? Number(data["Annual Revenue"]) : 0,
                "Employees 2025": data["Employees 2025"] ? Number(data["Employees 2025"]) : 0,
                "Annual Revenue 2025": data["Annual Revenue 2025"] ? Number(data["Annual Revenue 2025"]) : 0,
                "Employees 2024": data["Employees 2024"] ? Number(data["Employees 2024"]) : 0,
                "Annual Revenue 2024": data["Annual Revenue 2024"] ? Number(data["Annual Revenue 2024"]) : 0,
                last_validated_at: new Date(),
            },
        };
        axiosInstance.post(`/firms`, postData).then((res) => {
            if (res.status === 200) {
                setDrawerOpen(false);
                setRefreshTrigger(prev => prev + 1);
            } else {
                console.log("API Error");
            }
        });
    };

    const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'ascending' | 'descending' } | null>(null);

    const handleSort = (key: string) => {
        let direction: 'ascending' | 'descending' = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedCases = React.useMemo(() => {
        let sortableItems = [...cases];

        if (searchTerm) {
            const fuse = new Fuse(sortableItems, {
                keys: ['firmRecord.Company Name', 'firmRecord.City'],
                threshold: 0.3,
            });
            sortableItems = fuse.search(searchTerm).map(result => result.item);
        }

        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aValue: any;
                let bValue: any;

                if (sortConfig.key === 'lastActivityDate') {
                    aValue = new Date(a.lastActivityDate).getTime();
                    bValue = new Date(b.lastActivityDate).getTime();
                } else if (sortConfig.key.startsWith('firmRecord.')) {
                    const field = sortConfig.key.split('.')[1];
                    aValue = a.firmRecord?.[field];
                    bValue = b.firmRecord?.[field];

                    if (['Employees', 'Employees 2025', 'Employees 2024', 'Annual Revenue', 'Annual Revenue 2025', 'Annual Revenue 2024'].includes(field)) {
                        aValue = aValue ? Number(aValue) : -Infinity;
                        bValue = bValue ? Number(bValue) : -Infinity;
                    }
                } else {
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
                }

                if (aValue === undefined || aValue === null) return 1;
                if (bValue === undefined || bValue === null) return -1;

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [cases, sortConfig, searchTerm]);

    const getSortIndicator = (key: string) => {
        if (!sortConfig || sortConfig.key !== key) {
            return null;
        }
        return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    return (
        <div className="p-4 bg-white rounded-lg shadow-md overflow-x-auto">
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search by Company Name or City..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <table className="min-w-full text-left text-sm font-light">
                <thead className="border-b font-medium dark:border-neutral-500">
                    <tr>
                        <th scope="col" className="px-2 py-2 w-1/4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.Company Name')}>
                            Company Name{getSortIndicator('firmRecord.Company Name')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-32 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('lastActivityDate')}>
                            Last Activity{getSortIndicator('lastActivityDate')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-32 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.City')}>
                            City{getSortIndicator('firmRecord.City')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-24 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.Employees')}>
                            Employees{getSortIndicator('firmRecord.Employees')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-24 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.Employees 2025')}>
                            2025 Employees{getSortIndicator('firmRecord.Employees 2025')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-24 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.Employees 2024')}>
                            2024 Employees{getSortIndicator('firmRecord.Employees 2024')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-32 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.Annual Revenue')}>
                            Revenue{getSortIndicator('firmRecord.Annual Revenue')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-32 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.Annual Revenue 2025')}>
                            2025 Revenue{getSortIndicator('firmRecord.Annual Revenue 2025')}
                        </th>
                        <th scope="col" className="px-2 py-2 w-32 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('firmRecord.Annual Revenue 2024')}>
                            2024 Revenue{getSortIndicator('firmRecord.Annual Revenue 2024')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedCases.map((item) => {
                        return (
                            <tr key={item._id} className="border-b dark:border-neutral-500 hover:bg-neutral-100 cursor-pointer" onClick={() => handleRowClick(item)}>
                                <td className="whitespace-normal px-2 py-2 break-words max-w-xs">{item.firmRecord?.["Company Name"]}</td>
                                <td className="whitespace-nowrap px-2 py-2">{new Date(item.lastActivityDate).toLocaleDateString()}</td>
                                <td className="whitespace-nowrap px-2 py-2">{item.firmRecord?.City}</td>
                                <td className="whitespace-nowrap px-2 py-2">{item.firmRecord?.Employees}</td>
                                <td className="whitespace-nowrap px-2 py-2">{item.firmRecord?.["Employees 2025"]}</td>
                                <td className="whitespace-nowrap px-2 py-2">{item.firmRecord?.["Employees 2024"]}</td>
                                <td className="whitespace-nowrap px-2 py-2">{formatCurrency(item.firmRecord?.["Annual Revenue"])}</td>
                                <td className="whitespace-nowrap px-2 py-2">{formatCurrency(item.firmRecord?.["Annual Revenue 2025"])}</td>
                                <td className="whitespace-nowrap px-2 py-2">{formatCurrency(item.firmRecord?.["Annual Revenue 2024"])}</td>
                            </tr>
                        )
                    })}
                    {cases.length === 0 && (
                        <tr>
                            <td colSpan={9} className="text-center py-2">Loading or no data available...</td>
                        </tr>
                    )}
                </tbody>
            </table>

            <DataValidationDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                firm={selectedCase?.firmRecord}
                onVerify={(data) => {
                    handleVerify(data);
                }}
                onEmailClick={() => {
                    if (selectedCase?.firmRecord?.Email) {
                        setDirectEmailOpen(true);
                    }
                }}
            >
                {selectedCase && (
                    <div className="p-4 border-t bg-white">
                        <StyledButtonMain
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onClick={() => window.open(`/case?id=${selectedCase._id}`, '_blank')}
                            size="large"
                        >
                            Go to Case
                        </StyledButtonMain>
                    </div>
                )}
            </DataValidationDrawer>
            <ThriveModal
                height={750}
                open={directEmailOpen}
                contentComponent={
                    <EmailEditor
                        theSubject={""}
                        setOpen={setDirectEmailOpen}
                        theCase={selectedCase || {}}
                        theFirm={selectedCase?.firmRecord ? { ...selectedCase.firmRecord, openCase: selectedCase._id } : {}}
                        theReferral={{}}
                        editorTitle={"Direct Email"}
                        bodyGenerator={DirectEmailEmptyBody}
                        setMessageOpen={setMessageOpen}
                        setMessageTitle={setMessageTitle}
                        setMessageBody={setMessageBody}
                        theBFC={{}}
                        theSelectedEvent={{}}
                        theSelectedProvider={{}}
                    ></EmailEditor>
                }
            ></ThriveModal>
            <ThriveModal
                open={messageOpen}
                setOpen={setMessageOpen}
                closeByClick={true}
                height={250}
                width={500}
                contentComponent={
                    <NoticeDialog
                        title={messageTitle}
                        body={messageBody}
                        setOpen={setMessageOpen}
                        dataFlag={false}
                        setDataFlag={() => { }}
                    ></NoticeDialog>
                }
            ></ThriveModal>
        </div>
    );
};

export default DataValidations;